import React, { useState } from 'react';
import {
    BygrProfileDetails,
    ProfileTreeMember,
    ProfileTreeNode,
    ProfileTreeProject,
    ProfileTreeProjectMember,
    ProjectMemberSource,
} from '@/types';
import { Button, CustomLink, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';

const MONO: React.CSSProperties = {
    fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
    whiteSpace: 'pre',
};
import { useAsyncState } from '@/utils/hooks';
import {
    getProfileDescendants,
    setAnalyticsIsEnabled,
    setProfileParent,
} from '@/services/profiles';
import {
    removeInheritedMember,
    removeInheritedMemberFromProject,
} from '@/services/inheritedMembers';
import MakeWatcherConfirmModal from './MakeWatcherConfirmModal';
import AddProfileModal from '../AddProfileModal';
import theme from './ProfileHierarchy.module.scss';

interface Props {
    profile: BygrProfileDetails;
    reloadProfile(): void;
}

const ProfileHierarchy: React.FC<Props> = ({ profile, reloadProfile }) => {
    const [tree, , reloadTree] = useAsyncState(() => getProfileDescendants(profile.id), [profile.id], null);
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [collapsedProfiles, setCollapsedProfiles] = useState<Set<number>>(new Set());
    const [membersOpen, setMembersOpen] = useState<Set<number>>(new Set());
    const [projectsOpen, setProjectsOpen] = useState<Set<number>>(new Set());
    const [projectMembersOpen, setProjectMembersOpen] = useState<Set<number>>(new Set());
    const [inheritedListOpen, setInheritedListOpen] = useState<Set<number>>(new Set());
    const [makeWatcherFor, setMakeWatcherFor] = useState<{ userId: number; userName: string; mode: 'add' | 'edit' } | null>(null);

    const makeToggler = (setter: React.Dispatch<React.SetStateAction<Set<number>>>) => (id: number) => {
        setter((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleCollapse = makeToggler(setCollapsedProfiles);
    const toggleMembers = makeToggler(setMembersOpen);
    const toggleProjects = makeToggler(setProjectsOpen);
    const toggleProjectMembers = makeToggler(setProjectMembersOpen);
    const toggleInheritedList = makeToggler(setInheritedListOpen);

    const toggleNodeAnalytics = async (node: ProfileTreeNode) => {
        await setAnalyticsIsEnabled(node.id, !node.analyticsEnabled);
        reloadTree();
        if (node.id === profile.id) reloadProfile();
    };

    // Opens the preview-and-confirm modal. Adding happens inside the modal.
    const handleMakeInheritedMember = (userId: number) => {
        const member = findMemberInRoot(tree, userId);
        setMakeWatcherFor({ userId, userName: member?.userName || '', mode: 'add' });
    };

    const handleEditInheritedMember = (userId: number) => {
        const member = findMemberInRoot(tree, userId);
        setMakeWatcherFor({ userId, userName: member?.userName || '', mode: 'edit' });
    };

    const handleRemoveInheritedMember = async (userId: number) => {
        const proceed = window.confirm(
            'Remove this user as a global member of the whole tree? They will lose access to every project under this profile.'
        );
        if (!proceed) return;
        try {
            await removeInheritedMember(profile.id, userId);
        } catch (e: any) {
            alert(e?.message || 'Failed to remove global member.');
        }
        reloadTree();
    };

    const handleDetachProfile = async (childId: number, childName: string) => {
        const proceed = window.confirm(
            `Detach "${childName}" from this tree? It becomes a top-level profile. Every global user inherited from this tree's ancestors is removed from "${childName}" and its descendants (their access to non-inherited memberships is not affected). This cannot be undone from the UI.`
        );
        if (!proceed) return;
        try {
            await setProfileParent(childId, null);
        } catch (e: any) {
            alert(e?.message || 'Failed to detach profile.');
        }
        reloadTree();
    };

    const handleRemoveInheritedMemberFromProject = async (userId: number, projectId: number, projectName: string) => {
        const proceed = window.confirm(
            `Remove this global member from "${projectName}" only? They keep their global access on every other project in the tree. Future "Add globally" runs will not re-add them to this specific project.`
        );
        if (!proceed) return;
        try {
            await removeInheritedMemberFromProject(profile.id, userId, projectId);
        } catch (e: any) {
            alert(e?.message || 'Failed to remove global member from project.');
        }
        reloadTree();
    };

    if (!tree) return <LoadingIndicator />;

    const descendantIds: number[] = [];
    const allProjectIds: number[] = [];
    const allUserIds = new Set<number>();
    const walk = (n: ProfileTreeNode) => {
        descendantIds.push(n.id);
        n.members.forEach((m) => allUserIds.add(m.userId));
        n.projects.forEach((p) => {
            allProjectIds.push(p.id);
            p.members.forEach((m) => allUserIds.add(m.userId));
        });
        n.children.forEach(walk);
    };
    walk(tree);

    const hasDescendants = tree.children.length > 0;

    // Build lists per user (only Inherited-source memberships): projects + profiles.
    const inheritedProjectsByUser = new Map<number, string[]>();
    const inheritedProfilesByUser = new Map<number, string[]>();
    const collectInherited = (n: ProfileTreeNode) => {
        n.members.forEach((m) => {
            if (m.source === ProjectMemberSource.Inherited) {
                const list = inheritedProfilesByUser.get(m.userId) || [];
                list.push(n.name);
                inheritedProfilesByUser.set(m.userId, list);
            }
        });
        n.projects.forEach((p) =>
            p.members.forEach((m) => {
                if (m.source === ProjectMemberSource.Inherited) {
                    const list = inheritedProjectsByUser.get(m.userId) || [];
                    list.push(p.name);
                    inheritedProjectsByUser.set(m.userId, list);
                }
            })
        );
        n.children.forEach(collectInherited);
    };
    collectInherited(tree);

    const expandAll = () => {
        setCollapsedProfiles(new Set());
        setMembersOpen(new Set(descendantIds));
        setProjectsOpen(new Set(descendantIds));
        setProjectMembersOpen(new Set(allProjectIds));
        setInheritedListOpen(new Set(allUserIds));
    };

    const collapseAll = () => {
        setCollapsedProfiles(new Set(descendantIds));
        setMembersOpen(new Set());
        setProjectsOpen(new Set());
        setProjectMembersOpen(new Set());
        setInheritedListOpen(new Set());
    };

    return (
        <>
            {/* Row hover + separator for the tree — helps trace name→action on wide monitors */}
            <style>{`
                .hier-row { border-bottom: 1px solid #f0f0f0; }
                .hier-row:hover { background: #eef3fa !important; }
            `}</style>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button color="primary" onClick={() => setIsAddOpen(true)}>
                    Add child profile
                </Button>
                {hasDescendants && (
                    <>
                        <Button size="small" color="tertiary" onClick={expandAll}>
                            Expand all
                        </Button>
                        <Button size="small" color="tertiary" onClick={collapseAll}>
                            Collapse all
                        </Button>
                    </>
                )}
            </div>
            <div>
                <ProfileRow
                    key={tree.id}
                    node={tree}
                    depth={0}
                    isRoot
                    collapsedProfiles={collapsedProfiles}
                    membersOpen={membersOpen}
                    projectsOpen={projectsOpen}
                    projectMembersOpen={projectMembersOpen}
                    onToggleCollapse={toggleCollapse}
                    onToggleMembers={toggleMembers}
                    onToggleProjects={toggleProjects}
                    onToggleProjectMembers={toggleProjectMembers}
                    onToggleAnalytics={toggleNodeAnalytics}
                    inheritedProjectsByUser={inheritedProjectsByUser}
                    inheritedProfilesByUser={inheritedProfilesByUser}
                    inheritedListOpen={inheritedListOpen}
                    onToggleInheritedList={toggleInheritedList}
                    onMakeInheritedMember={handleMakeInheritedMember}
                    onEditInheritedMember={handleEditInheritedMember}
                    onRemoveInheritedMember={handleRemoveInheritedMember}
                    onRemoveInheritedMemberFromProject={handleRemoveInheritedMemberFromProject}
                    onDetachProfile={handleDetachProfile}
                />
            </div>
            {!hasDescendants && (
                <p style={{ color: '#777', marginTop: 12 }}>No child profiles yet.</p>
            )}
            <AddProfileModal
                isOpen={isAddOpen}
                toggle={() => setIsAddOpen(false)}
                onSave={reloadTree}
                parentProfileId={profile.id}
                parentProfileName={profile.name}
                excludeProfileIds={descendantIds}
            />
            {makeWatcherFor && (
                <MakeWatcherConfirmModal
                    isOpen={!!makeWatcherFor}
                    profileId={profile.id}
                    profileName={profile.name}
                    userId={makeWatcherFor.userId}
                    userDisplayName={makeWatcherFor.userName}
                    mode={makeWatcherFor.mode}
                    onClose={() => setMakeWatcherFor(null)}
                    onSuccess={() => {
                        reloadTree();
                        reloadProfile();
                    }}
                />
            )}
        </>
    );
};

// Helper: find a member in the root profile by userId.
function findMemberInRoot(tree: ProfileTreeNode | null, userId: number) {
    if (!tree) return null;
    return tree.members.find((m) => m.userId === userId) || null;
}

interface ProfileRowProps {
    node: ProfileTreeNode;
    depth: number;
    isRoot?: boolean;
    collapsedProfiles: Set<number>;
    membersOpen: Set<number>;
    projectsOpen: Set<number>;
    projectMembersOpen: Set<number>;
    onToggleCollapse(id: number): void;
    onToggleMembers(id: number): void;
    onToggleProjects(id: number): void;
    onToggleProjectMembers(projectId: number): void;
    onToggleAnalytics(node: ProfileTreeNode): void;
    inheritedProjectsByUser: Map<number, string[]>;
    inheritedProfilesByUser: Map<number, string[]>;
    inheritedListOpen: Set<number>;
    onToggleInheritedList(userId: number): void;
    onMakeInheritedMember(userId: number): void;
    onEditInheritedMember(userId: number): void;
    onRemoveInheritedMember(userId: number): void;
    onRemoveInheritedMemberFromProject(userId: number, projectId: number, projectName: string): void;
    onDetachProfile(childId: number, childName: string): void;
}

const ProfileRow: React.FC<ProfileRowProps> = (props) => {
    const {
        node,
        depth,
        isRoot,
        collapsedProfiles,
        membersOpen,
        projectsOpen,
        projectMembersOpen,
        onToggleCollapse,
        onToggleMembers,
        onToggleProjects,
        onToggleProjectMembers,
        onToggleAnalytics,
        inheritedProjectsByUser,
        inheritedProfilesByUser,
        inheritedListOpen,
        onToggleInheritedList,
        onMakeInheritedMember,
        onEditInheritedMember,
        onRemoveInheritedMember,
        onRemoveInheritedMemberFromProject,
        onDetachProfile,
    } = props;

    const hasChildren = node.children.length > 0;
    const isCollapsed = collapsedProfiles.has(node.id);
    const isMembersOpen = membersOpen.has(node.id);
    const isProjectsOpen = projectsOpen.has(node.id);
    const hasMembers = node.members && node.members.length > 0;
    const hasProjects = node.projects && node.projects.length > 0;
    const indent = 8 + depth * 20;

    return (
        <div>
            <div
                className="hier-row"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '4px 8px',
                    paddingLeft: indent,
                    gap: 8,
                    background: isRoot ? '#fafbfd' : '#fff',
                }}>
                <span
                    onClick={() => hasChildren && onToggleCollapse(node.id)}
                    style={{
                        cursor: hasChildren ? 'pointer' : 'default',
                        width: 14,
                        display: 'inline-block',
                        color: '#888',
                        userSelect: 'none',
                        ...MONO,
                    }}>
                    {hasChildren ? (isCollapsed ? '▶' : '▼') : ' '}
                </span>
                <strong style={{ fontSize: 14 }}>
                    <CustomLink to={`/profiles/${node.id}/dashboard`}>{node.name}</CustomLink>
                </strong>
                <CountToggle
                    count={node.numberOfMembers}
                    singular="member"
                    open={isMembersOpen}
                    enabled={hasMembers}
                    onClick={() => onToggleMembers(node.id)}
                />
                <CountToggle
                    count={node.projects.length}
                    singular="project"
                    open={isProjectsOpen}
                    enabled={hasProjects}
                    onClick={() => onToggleProjects(node.id)}
                />
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
                    <AnalyticsBadge node={node} onClick={() => onToggleAnalytics(node)} />
                    {node.isPaying && <Pill text="Paying" bg="#e8f5e9" fg="#1b5e20" />}
                    {!isRoot && (
                        <button
                            onClick={() => onDetachProfile(node.id, node.name)}
                            className={theme.detachButton}
                            title="Detach from parent (becomes top-level profile)">
                            Detach
                        </button>
                    )}
                </span>
            </div>

            {isMembersOpen && hasMembers && (
                <div style={{ paddingLeft: indent + 24, background: '#fafafa' }}>
                    {node.members.map((m, i) => (
                        <ProfileMemberRow
                            key={m.profileMemberId}
                            member={m}
                            isLast={i === node.members.length - 1}
                            showInheritedAction={isRoot}
                            inheritedProjects={inheritedProjectsByUser.get(m.userId) || []}
                            inheritedProfiles={inheritedProfilesByUser.get(m.userId) || []}
                            inheritedListExpanded={inheritedListOpen.has(m.userId)}
                            onToggleInheritedList={onToggleInheritedList}
                            onMakeInheritedMember={onMakeInheritedMember}
                            onEditInheritedMember={onEditInheritedMember}
                            onRemoveInheritedMember={onRemoveInheritedMember}
                        />
                    ))}
                </div>
            )}

            {isProjectsOpen && hasProjects && (
                <div style={{ paddingLeft: indent + 24, background: '#f6f9ff' }}>
                    {node.projects.map((p, i) => (
                        <ProjectRow
                            key={p.id}
                            project={p}
                            isLast={i === node.projects.length - 1}
                            isMembersOpen={projectMembersOpen.has(p.id)}
                            onToggleMembers={onToggleProjectMembers}
                            onRemoveInheritedMemberFromProject={onRemoveInheritedMemberFromProject}
                        />
                    ))}
                </div>
            )}

            {hasChildren && !isCollapsed && (
                <div>
                    {node.children.map((child) => (
                        <ProfileRow {...props} key={child.id} node={child} depth={depth + 1} isRoot={false} />
                    ))}
                </div>
            )}
        </div>
    );
};

const CountToggle: React.FC<{
    count: number;
    singular: string;
    open: boolean;
    enabled: boolean;
    onClick(): void;
}> = ({ count, singular, open, enabled, onClick }) => (
    <span
        onClick={() => enabled && onClick()}
        title={enabled ? `Click to show/hide ${singular}s` : `No ${singular}s`}
        style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 3,
            color: enabled ? '#555' : '#bbb',
            fontSize: 12,
            cursor: enabled ? 'pointer' : 'default',
            padding: '1px 6px',
            borderRadius: 10,
            background: enabled && open ? '#eef2f8' : 'transparent',
            border: '1px solid',
            borderColor: enabled && open ? '#cdd8eb' : 'transparent',
        }}>
        <strong style={{ color: enabled ? '#333' : '#bbb' }}>{count}</strong>
        <span>{singular}{count === 1 ? '' : 's'}</span>
        {enabled && <span style={{ ...MONO, fontSize: 9, marginLeft: 1 }}>{open ? '▾' : '▸'}</span>}
    </span>
);

const Pill: React.FC<{ text: string; bg: string; fg: string }> = ({ text, bg, fg }) => (
    <span
        style={{
            fontSize: 11,
            fontWeight: 600,
            padding: '1px 8px',
            background: bg,
            color: fg,
            borderRadius: 10,
            letterSpacing: 0.2,
        }}>
        {text}
    </span>
);

const RoleTag: React.FC<{ label: string; bg: string; fg: string }> = ({ label, bg, fg }) => (
    <span
        style={{
            display: 'inline-block',
            minWidth: 60,
            textAlign: 'center',
            fontSize: 10,
            fontWeight: 700,
            padding: '1px 6px',
            background: bg,
            color: fg,
            borderRadius: 3,
            letterSpacing: 0.3,
            flexShrink: 0,
            ...MONO,
        }}>
        {label.toUpperCase()}
    </span>
);

interface ProfileMemberRowProps {
    member: ProfileTreeMember;
    isLast: boolean;
    showInheritedAction?: boolean;
    inheritedProjects?: string[];
    inheritedProfiles?: string[];
    inheritedListExpanded?: boolean;
    onToggleInheritedList?(userId: number): void;
    onMakeInheritedMember?(userId: number): void;
    onEditInheritedMember?(userId: number): void;
    onRemoveInheritedMember?(userId: number): void;
}

const ProfileMemberRow: React.FC<ProfileMemberRowProps> = ({
    member,
    isLast,
    showInheritedAction,
    inheritedProjects = [],
    inheritedProfiles = [],
    inheritedListExpanded,
    onToggleInheritedList,
    onMakeInheritedMember,
    onEditInheritedMember,
    onRemoveInheritedMember,
}) => {
    let label = 'Member', bg = '#eee', fg = '#444';
    if (member.isOwner) { label = 'Owner'; bg = '#fde9c2'; fg = '#7a4a00'; }
    else if (member.isAdmin) { label = 'Admin'; bg = '#d6eaff'; fg = '#0b3d8a'; }

    const isInherited = inheritedProjects.length > 0 || inheritedProfiles.length > 0;

    return (
        <>
            <div
                className="hier-row"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 13,
                    padding: '3px 8px',
                    flexWrap: 'nowrap',
                }}>
                <span style={{ color: '#bbb', flexShrink: 0, ...MONO }}>{isLast ? '└─' : '├─'}</span>
                <RoleTag label={label} bg={bg} fg={fg} />
                <CustomLink to={`/users/${member.userId}`}>{member.userName || '(no name)'}</CustomLink>
                {showInheritedAction && (
                    <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        {isInherited && (
                            <span
                                onClick={() => onToggleInheritedList && onToggleInheritedList(member.userId)}
                                style={{
                                    fontSize: 11,
                                    color: '#225',
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                    padding: '2px 6px',
                                    background: inheritedListExpanded ? '#eef' : 'transparent',
                                    borderRadius: 3,
                                }}>
                                <span style={MONO}>{inheritedListExpanded ? '▾' : '▸'}</span>
                                {' '}global on {inheritedProfiles.length} profile
                                {inheritedProfiles.length === 1 ? '' : 's'}
                                {' · '}{inheritedProjects.length} project
                                {inheritedProjects.length === 1 ? '' : 's'}
                            </span>
                        )}
                        {isInherited ? (
                            <>
                                <Button
                                    size="xsmall"
                                    color="tertiary"
                                    onClick={() => onEditInheritedMember && onEditInheritedMember(member.userId)}>
                                    ✎ Edit globally
                                </Button>
                                <Button
                                    size="xsmall"
                                    color="tertiary"
                                    onClick={() => onRemoveInheritedMember && onRemoveInheritedMember(member.userId)}>
                                    ✕ Remove globally
                                </Button>
                            </>
                        ) : (
                            <Button
                                size="xsmall"
                                color="tertiary"
                                onClick={() => onMakeInheritedMember && onMakeInheritedMember(member.userId)}>
                                + Add globally
                            </Button>
                        )}
                    </span>
                )}
            </div>
            {showInheritedAction && isInherited && inheritedListExpanded && (
                <div
                    style={{
                        padding: '6px 12px 8px 32px',
                        background: '#f7f9fc',
                        fontSize: 12,
                        color: '#444',
                        borderBottom: '1px dashed #dde',
                        display: 'flex',
                        gap: 24,
                        flexWrap: 'wrap',
                    }}>
                    {inheritedProfiles.length > 0 && (
                        <div style={{ minWidth: 200 }}>
                            <div style={{ marginBottom: 4, color: '#777' }}>
                                Profiles ({inheritedProfiles.length}):
                            </div>
                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                {inheritedProfiles.map((name) => (
                                    <li key={name}>{name}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {inheritedProjects.length > 0 && (
                        <div style={{ minWidth: 200 }}>
                            <div style={{ marginBottom: 4, color: '#777' }}>
                                Projects ({inheritedProjects.length}):
                            </div>
                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                {inheritedProjects.map((name) => (
                                    <li key={name}>{name}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

const ProjectRow: React.FC<{
    project: ProfileTreeProject;
    isLast: boolean;
    isMembersOpen: boolean;
    onToggleMembers(projectId: number): void;
    onRemoveInheritedMemberFromProject(userId: number, projectId: number, projectName: string): void;
}> = ({ project, isLast, isMembersOpen, onToggleMembers, onRemoveInheritedMemberFromProject }) => {
    const hasMembers = project.members.length > 0;
    return (
        <div>
            <div
                className="hier-row"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 13,
                    padding: '3px 8px',
                }}>
                <span style={{ color: '#bbb', ...MONO }}>{isLast ? '└─' : '├─'}</span>
                <CustomLink to={`/projects/${project.id}`}>{project.name}</CustomLink>
                <CountToggle
                    count={project.numberOfMembers}
                    singular="member"
                    open={isMembersOpen}
                    enabled={hasMembers}
                    onClick={() => onToggleMembers(project.id)}
                />
                <span style={{ marginLeft: 'auto', fontSize: 11, color: '#888', ...MONO }}>
                    {moduleFlagsLabel(project.sellerModules)}
                </span>
            </div>
            {isMembersOpen && hasMembers && (
                <div style={{ paddingLeft: 30, background: '#fff' }}>
                    {project.members.map((m, i) => (
                        <ProjectMemberRow
                            key={m.projectMemberId}
                            member={m}
                            isLast={i === project.members.length - 1}
                            projectId={project.id}
                            projectName={project.name}
                            onRemoveInheritedMemberFromProject={onRemoveInheritedMemberFromProject}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

interface ProjectMemberRowProps {
    member: ProfileTreeProjectMember;
    isLast: boolean;
    projectId: number;
    projectName: string;
    onRemoveInheritedMemberFromProject(userId: number, projectId: number, projectName: string): void;
}

const ProjectMemberRow: React.FC<ProjectMemberRowProps> = ({
    member,
    isLast,
    projectId,
    projectName,
    onRemoveInheritedMemberFromProject,
}) => {
    const isInherited = member.source === ProjectMemberSource.Inherited;
    let label = 'Member', bg = '#eee', fg = '#444';
    if (member.isOwner) { label = 'Owner'; bg = '#fde9c2'; fg = '#7a4a00'; }
    else if (isInherited) {
        // Inherited members carry two orthogonal flags. Surface both at a glance.
        const role = member.isInheritedAdmin ? 'Admin' : 'Member';
        const analytics = member.hasInheritedAnalytics ? ' + Analytics' : '';
        label = `🌐 ${role}${analytics}`;
        if (member.isInheritedAdmin) { bg = '#c7d8f6'; fg = '#0a2540'; }
        else if (member.hasInheritedAnalytics) { bg = '#eef'; fg = '#225'; }
        else { bg = '#e5e7eb'; fg = '#374151'; }
    }
    else if (member.isAdmin) { label = 'Admin'; bg = '#d6eaff'; fg = '#0b3d8a'; }

    return (
        <div
            className="hier-row"
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 13,
                padding: '3px 8px',
                flexWrap: 'nowrap',
            }}>
            <span style={{ color: '#bbb', flexShrink: 0, ...MONO }}>{isLast ? '└─' : '├─'}</span>
            <RoleTag label={label} bg={bg} fg={fg} />
            <CustomLink to={`/users/${member.userId}`}>{member.userName || '(no name)'}</CustomLink>
            <span style={{ color: '#aaa', fontSize: 11, marginLeft: 'auto', flexShrink: 0, ...MONO }}>
                {moduleFlagsLabel(member.bygrModules)}
            </span>
            {isInherited && (
                <Button
                    size="xsmall"
                    color="tertiary"
                    onClick={() => onRemoveInheritedMemberFromProject(member.userId, projectId, projectName)}>
                    ✕
                </Button>
            )}
        </div>
    );
};

// BygrModule flags: ChangeManagement=1, Aftermarket=2, QualityManagement=4
function moduleFlagsLabel(flags: number): string {
    const parts: string[] = [];
    if (flags & 1) parts.push('CM');
    if (flags & 2) parts.push('AM');
    if (flags & 4) parts.push('QM');
    return parts.length ? parts.join('·') : '—';
}

const AnalyticsBadge: React.FC<{ node: ProfileTreeNode; onClick(): void }> = ({ node, onClick }) => {
    const on = node.analyticsEnabled;
    const bg = on ? '#e8f5e9' : '#f5f5f5';
    const fg = on ? '#1b5e20' : '#888';

    return (
        <span
            onClick={onClick}
            title={`Analytics ${on ? 'ON' : 'OFF'} — click to toggle.`}
            style={{
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 600,
                padding: '1px 8px',
                background: bg,
                color: fg,
                borderRadius: 10,
                letterSpacing: 0.2,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
            }}>
            Analytics {on ? 'ON' : 'OFF'}
        </span>
    );
};

export default ProfileHierarchy;
